import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { Form, Button, Input, Tag, Empty, Upload } from 'antd';

import {
  Attachments,
  QuotationStatus,
  QuotationSupplier,
  QuotationSupplierStatus,
  QuotationSuppliersLabel,
  SendQuotationRequest,
} from '@/core/domain/stock/Quotations/Quotations.types';
import {
  useQuotationSuppliers,
  useSendQuotation,
} from '@/core/domain/stock/Quotations/Quotations.hooks';

import Table, { ColumnsType } from '@/components/_common/Table';
import FloatingFormItem from '@/components/_common/FloatingFormItem';

import { useQuotationContext } from '..';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { UploadCloud } from 'react-feather';
import { RcFile } from 'antd/lib/upload';
import { useState } from 'react';
import { convertFileToBase64 } from '@/utils/convertFileToBase64';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { getTableScrollX } from '@/utils/table';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { FaWhatsapp } from 'react-icons/fa';
import { useDisclosure } from '@/hooks';
import QuotationWhatsappModal from './QuotationWhatsappModal';

interface SendQuotationFormData {
  subject: string;
  suppliers: {
    id: number;
    email: string;
  }[];
  content: string;
  attachments: Attachments[];
}

const QuotationDetailsRequestQuotationPage = () => {
  const intl = useIntl();
  const [form] = Form.useForm<SendQuotationFormData>();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;
  const quotation = useQuotationContext();

  const [fileList, setFileList] = useState<RcFile[]>([]);

  const [modalOpen, motalActions] = useDisclosure(false);

  const [quotationSelect, setQuotationSelect] = useState<number>();
  const [quotationCode, setQuotationCode] = useState<string>();

  const {
    data: quotationSuppliersData,
    isFetching: quotationSuppliersIsLoading,
  } = useQuotationSuppliers(+quotationId, true);

  const sendRequestMutation = useSendQuotation(+quotationId);

  const handleSendClick = async () => {
    try {
      if (fileList.length) {
        const arrayPromisses = await fileList.map(
          async (file) => await convertFileToBase64(file),
        );

        await Promise.all(arrayPromisses).then((valuesPromisse) => {
          form.setFieldsValue({
            attachments: valuesPromisse.map((base64, index) => {
              const fileName = fileList[index].name;

              return {
                content: base64,
                fileName,
                contentType: fileName.split('.').at(-1)?.toUpperCase(),
              };
            }),
          });
        });

        handleSendQuotation();
      } else {
        form.setFieldsValue({ attachments: [] });

        handleSendQuotation();
      }
    } catch {}
  };

  const handleSendQuotation = async () => {
    const result = await form.validateFields();

    const obj: SendQuotationRequest = {
      ...result,
    };

    await sendRequestMutation.mutateAsync(obj);
  };

  const columns: ColumnsType<QuotationSupplier> = [
    {
      key: 'name',
      ellipsis: true,
      width: 150,
      dataIndex: 'name',
      title: intl.formatMessage({
        id: 'quotations.details.suppliers',
      }),
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      key: 'email',
      dataIndex: 'email',
      ellipsis: true,
      width: 180,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.email',
      }),
      render: (value, row, i) => (
        <>
          <Form.Item
            noStyle
            hidden
            name={['suppliers', i, 'supplierId']}
            initialValue={row.supplierId}
          >
            <Input />
          </Form.Item>
          <Form.Item
            noStyle
            name={['suppliers', i, 'supplierEmail']}
            initialValue={value}
          >
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'status',
      dataIndex: 'status',
      ellipsis: true,
      align: 'center',
      width: 150,
      title: intl.formatMessage({
        id: 'quotations.details.suppliers.status',
      }),
      render: (value: QuotationSupplierStatus) => (
        <Tag color="processing">
          <FormattedMessage
            id={`quotations.${QuotationSuppliersLabel[value]}`}
          />
        </Tag>
      ),
    },
    {
      key: 'actions',
      dataIndex: 'actions',
      title: intl.formatMessage({ id: 'customers.list.header.actions' }),
      render: (_, row) => (
        <FaWhatsapp
          onClick={() => {
            setQuotationSelect(row.supplierId);
            setQuotationCode(row.code);
            motalActions.open();
          }}
          size={20}
        />
      ),
      ellipsis: true,
      width: 30,
    },
  ];

  return (
    <div>
      <div className="create-page-title">
        <SpaceForButton justifyContent="space-between">
          <h2>
            <FormattedMessage id="quotations.details.sendQuotationRequest" />
          </h2>
          <div>
            <Button
              block
              type="primary"
              onClick={handleSendClick}
              disabled={
                quotation?.disabled ||
                !hasPermissions([Authorities.MOVIMENTATION_QUOTATION_SEND])
              }
              loading={sendRequestMutation.isLoading}
            >
              <FormattedMessage id="quotations.details.sendQuotationRequest.send" />
            </Button>
          </div>
        </SpaceForButton>
      </div>

      {quotation?.status === QuotationStatus.AwaitingOffers ? (
        <Empty description={'Aguardando resposta do(s) fornecedor(es)'} />
      ) : (
        <>
          <Form form={form}>
            <Table
              loading={quotationSuppliersIsLoading}
              rowKey={(row) => row.supplierId}
              dataSource={quotationSuppliersData?.result?.data}
              columns={columns}
              scroll={{ x: getTableScrollX(columns) }}
              pagination={false}
            />

            <div style={{ margin: '32px 0' }}>
              <FloatingFormItem
                name="subject"
                label={intl.formatMessage({
                  id: 'quotations.details.sendQuotationRequest.subject',
                })}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.requiredField',
                    }),
                  },
                ]}
              >
                <Input />
              </FloatingFormItem>
              <FloatingFormItem
                name="content"
                label={intl.formatMessage({
                  id: 'quotations.details.sendQuotationRequest.content',
                })}
              >
                <Input.TextArea rows={5} />
              </FloatingFormItem>

              <Form.Item
                noStyle
                name="attachments"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload.Dragger
                  beforeUpload={(file) => {
                    setFileList((prev) => [...prev, file]);
                    return false;
                  }}
                  onRemove={({ uid }) => {
                    setFileList((prev) => {
                      if (prev.length) {
                        return prev.filter((file) => file.uid !== uid);
                      }
                      return [];
                    });
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <AntdFeatherIcon icon={<UploadCloud />} size={32} />
                    <p
                      style={{
                        marginTop: '12px',
                        fontSize: '1.05em',
                        fontWeight: '500',
                      }}
                    >
                      <FormattedMessage id="quotations.details.sendQuotationRequest.attachmentText" />
                    </p>
                    <p style={{ color: 'rgb(0, 0, 0, 0.5)', marginTop: '4px' }}>
                      <FormattedMessage id="quotations.details.sendQuotationRequest.attachmentHint" />
                    </p>
                  </div>
                </Upload.Dragger>
              </Form.Item>
            </div>
          </Form>
        </>
      )}

      <QuotationWhatsappModal
        open={modalOpen}
        onCancel={() => {
          motalActions.close();
          setQuotationCode(undefined);
          setQuotationSelect(undefined);
        }}
        supplierId={quotationSelect}
        quotationCode={quotationCode}
      />
    </div>
  );
};

export default QuotationDetailsRequestQuotationPage;
